import {ReactElement, useEffect, useState} from "react";
import { useAssociateUsersWithAcquisitionPropertyMutation, useGetUsersForAcquisitionPropertyLazyQuery, useUpdateAcquisitionPropertyMutation, VersionType } from '../../__generated__/generated_types';
import {ViziblyTheme} from "../analyst/ViziblyZDGTheme";
import {ThemeProvider} from "@zendeskgarden/react-theming";
import {Close, Modal} from "@zendeskgarden/react-modals";
import {toast} from "react-toastify";
import AcquisitionPropertyForm, {
    TAcquisitionPropertyRecord,
} from "./components/acquisition-property-form/AcquisitionPropertyForm";
import {AcquisitionProperty} from "./types";

export interface IEditAcquisitionProperty {
    property: AcquisitionProperty,
    formVisible: boolean,
    onModalClose: () => void,
    onUpdate: () => void,
}

export function AcquisitionPropertyEditModal(props: IEditAcquisitionProperty): ReactElement {
    const {
        property: propsProperty,
        formVisible,
        onModalClose,
        onUpdate,
    } = props;

    const [getUsersForProperty] = useGetUsersForAcquisitionPropertyLazyQuery({
        fetchPolicy: "no-cache",
        onCompleted: data => {
            if (data.getUsersForAcquisitionProperty) {
                setUsersForProperty(
                    data.getUsersForAcquisitionProperty.map(x => x.id)
                );
            }
        },
    });

    const [saveProperty, {loading: updatePropertyLoading}] = useUpdateAcquisitionPropertyMutation({
        notifyOnNetworkStatusChange: true,
    });
    const [saveUsersForProperty] = useAssociateUsersWithAcquisitionPropertyMutation({
        notifyOnNetworkStatusChange: true,
    });

    const [property, setProperty] = useState<AcquisitionProperty>(propsProperty);
    const [usersForProperty, setUsersForProperty] = useState<string[]>([]);

    function updateProperty(update: Partial<TAcquisitionPropertyRecord>): void {
        if (property) {
            setProperty({
                ...property,
                ...update,
            });
        }
    }

    function reset(): void {
        onModalClose();
    }

    async function doSaveProperty(): Promise<void> {
        if (property && property.id != undefined) {
            const propertyPromise = saveProperty({
                variables: {
                    id: property.id,
                    name: property.name,
                    isDeactivated: property.isDeactivated,
                    acquiredPropertyId: property.acquiredPropertyId,
                    acquisitionBudgetStartMonth: property.acquisitionBudgetStartMonth,
                    acquisitionBudgetYear: property.acquisitionBudgetYear,
                    acquisitionBudgetPeriod: property.acquisitionBudgetPeriod,
                    reforecastYear: property.reforecastYear,
                    revenueConfiguration: property.revenueConfiguration,
                    originalRevenueModelSubsidyEnabled: property.originalRevenueModelSubsidyEnabled
                }
            });

            const usersPromise = saveUsersForProperty({
                variables: {
                    propertyId: property.id,
                    userIds: usersForProperty,
                }
            });

            const [propertyResult, usersResult] = await Promise.allSettled([propertyPromise, usersPromise]);

            if (propertyResult.status == "fulfilled" && usersResult.status == "fulfilled") {
                toast.success("Property updated");
                onUpdate();
                reset();
            } else {
                toast.error("There was a problem saving the property");
            }
        }
    }

    useEffect(() => {
        getUsersForProperty({
            variables: {
                propertyId: property.id,
            }
        });
    }, []);

    if (!formVisible) {
        return <></>;
    }

    return (
        <ThemeProvider theme={ViziblyTheme}>
            <Modal onClose={reset}>
                { usersForProperty &&
                    <AcquisitionPropertyForm
                        property={{
                            ...property,
                            acquisitionBudgetPeriod: property.acquisitionBudgetPeriod as (VersionType.Budget | VersionType.Reforecast),
                            acquiredPropertyId: property.acquiredPropertyId ?? undefined,
                        }}
                        usersForProperty={usersForProperty}
                        loading={updatePropertyLoading}
                        onChange={updateProperty}
                        onUsersChange={setUsersForProperty}
                        onSubmit={doSaveProperty}
                        onClose={reset}
                    />
                }
                <Close aria-label="Close modal" />
            </Modal>
        </ThemeProvider>
    );
}